import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import prepareHeaders from '../utils/prepareHeaders'
import { getSelectedCustomerId, getSelectedOrFirstCustomerId } from '../selectors/customers'
import addParameters from '../utils/addParameters'

const base = '/secure'

export const secureApi = createApi({
  reducerPath: 'securePosture',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getCheck: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({ checkType, customerId, mfaUserDataOnly = false, mfaTenantDataOnly = false, caPolicyId }) =>
        addParameters(`${base}/check/${checkType}`, { customerId, mfaUserDataOnly, mfaTenantDataOnly, caPolicyId }),
    }),
    getSecurityPosture: builder.query({
      keepUnusedDataFor: Infinity,
      query: (customerId = '*') => `${base}/posture/${customerId}`,
    }),
    getAiReadinessReport: builder.query({
      query: (customerId) => `${base}/ai-readiness/${customerId}`,
    }),
    getConditionalAccessPolicies: builder.query({
      query: (customerId) => `${base}/policy/${customerId}/policies/cap`,
    }),
    getConditionalAccessPolicy: builder.query({
      query: ({ customerId, policyId }) => `${base}/policy/${customerId}/policies/cap/${policyId}`,
    }),
  }),
})

export const {
  useGetSecurityPostureQuery,
  useLazyGetSecurityPostureQuery,
  useGetAiReadinessReportQuery,
  useGetConditionalAccessPoliciesQuery,
  useLazyGetConditionalAccessPoliciesQuery,
  useGetConditionalAccessPolicyQuery,
  useLazyGetConditionalAccessPolicyQuery,
  useGetCheckQuery,
  useLazyGetCheckQuery,
} = secureApi

const MFA_CONDITIONAL_ACCESS_CONFIGURE_TYPES = new Set(['adminmfa', 'usermfa'])

export const getPolicyParameters = (customerId, checkType = 'adminmfa') => ({
  checkType,
  customerId,
  mfaUserDataOnly: false,
  mfaTenantDataOnly: MFA_CONDITIONAL_ACCESS_CONFIGURE_TYPES.has(checkType),
})

export const policyResponseSelector = (result) => {
  const { data, ...rest } = result

  if (!data) {
    return {
      ...rest,
      policyConfigurations: {},
      policiesList: [],
    }
  }

  const policyConfigurations = data.configure ?? {}

  return {
    ...rest,
    policyConfigurations,
    policiesList: policyConfigurations.data ?? [],
  }
}

export const useGetCustomerPolicies = () => {
  const customerId = useSelector(getSelectedCustomerId)
  const { data: policiesList = [], ...rest } = useGetConditionalAccessPoliciesQuery(customerId, {
    skip: !customerId,
  })

  return {
    data: policiesList,
    ...rest,
  }
}

export const useGetCustomerCheck = (checkType, queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  // If checkType is not provided, the query will be skipped via the skip option. However, we also want to skip if customerId is not available to avoid making a request with an invalid customerId
  const shouldSkip = Boolean(queryOptions.skip) || !customerId || !checkType

  return useGetCheckQuery(
    { checkType, customerId },
    {
      ...queryOptions,
      skip: shouldSkip,
    },
  )
}

export const useGetCustomerSecurityPosture = (supportAllCompanies = true) => {
  const customerId = useSelector(supportAllCompanies ? getSelectedCustomerId : getSelectedOrFirstCustomerId)

  return useGetSecurityPostureQuery(customerId)
}

export const useGetCustomerSecurityPostureChecks = () => {
  const result = useGetCustomerSecurityPosture(false)
  const { data } = result

  if (!data) {
    return result
  }

  const { securityChecks: { allMonitored = [], ignored = [] } = {} } = data

  return {
    ...result,
    data: [...allMonitored, ...ignored],
  }
}
